import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import NotFoundResults from './NotFoundResults'
import { List, ScrollSync } from 'react-virtualized'
import 'react-virtualized/styles.css'
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer'
import { CellMeasurer, CellMeasurerCache } from 'react-virtualized'

const MobileTable = ({
    headerTitle,
    headerSubtitle,
    headerActions,
    data,
    cols,
    selectedItems,
    query,
    isDashboardPage = false,
    onSelectCard,
    selectable = false,
}) => {
    const colsToHasSelect = 'assetName'
    const colToHasOptions = 'userName'
    const colsToUse = cols?.filter(
        c => c?.field !== 'select' && c?.field !== 'options',
    )
    const colsHasSelect = cols?.find(c => c?.field === 'select')
    const colsHasOptions = cols?.find(c => c?.field === 'options')
    const cache = new CellMeasurerCache({
        defaultHeight: 284,
        fixedWidth: true,
    })

    function rowRenderer({
        key, // Unique key within array of rows
        index: indexData, // Index of row within collection
        style, // Style object to be applied to row (to position it)
        parent,
    }) {
        const item = data[indexData]
        const renderCard = () => {
            const itemIsSelected = selectedItems?.find(
                r => r?.customId === item?.customId,
            )

            return (
                <CellMeasurer
                    cache={cache}
                    columnIndex={0}
                    key={key}
                    parent={parent}
                    rowIndex={indexData}
                >
                    <div className="w-full h-auto pb-2">
                        <div
                            key={`rowMobile-${indexData}`}
                            className="w-full h-full rounded-2xl border-grayLines overflow-hidden"
                            onClick={
                                selectable
                                    ? () => onSelectCard(item.customId)
                                    : () => null
                            }
                        >
                            {colsToUse?.map((col, indexCol) => {
                                const isCustomCell =
                                    col?.field === 'assetName' ||
                                    col?.field === 'requestNumber' ||
                                    col?.field === 'userName' ||
                                    col?.field === 'estimateNumber'
                                const isExpandedCell =
                                    col?.field === 'location' ||
                                    col?.field === 'status'
                                const bgColor =
                                    (indexCol % 2) - (isCustomCell ? 0 : 1) ===
                                        0
                                        ? 'bg-white'
                                        : 'bg-pgBackground'
                                const needsSelectOption =
                                    colsToHasSelect === col?.field &&
                                    colsHasSelect
                                const needsOptions =
                                    colToHasOptions === col?.field &&
                                    colsHasOptions
                                const isStatusBadge =
                                    col?.field === 'status' ||
                                    col?.field === 'statusC'
                                return (
                                    <div
                                        key={`col-row${indexData}-${indexCol}`}
                                        className={clsx(
                                            needsOptions && 'overflow-visible',
                                            'w-full flex flex-row justify-start',
                                            !isCustomCell && 'p-4',
                                            isExpandedCell || isStatusBadge
                                                ? 'h-auto items-start'
                                                : 'h-13 items-center',
                                            needsSelectOption && itemIsSelected
                                                ? 'bg-white py-0'
                                                : bgColor,
                                        )}
                                    >
                                        {!isCustomCell && (
                                            <p
                                                className={clsx(
                                                    'text-sm font-medium text-textDarkGray',
                                                )}
                                            >
                                                {col?.value}
                                            </p>
                                        )}
                                        <div
                                            className={clsx(
                                                'flex-grow w-full font-normal flex items-center justify-end max-w-full text-textDarkerGray transition-all',
                                                needsOptions &&
                                                'overflow-visible',
                                                !needsOptions && 'truncate',
                                                needsSelectOption ||
                                                (needsOptions && 'gap-x-2'),
                                                isCustomCell &&
                                                'p-4 border-b border-grayLines',
                                                needsSelectOption &&
                                                itemIsSelected &&
                                                isCustomCell &&
                                                'bg-yellow bg-opacity-5',
                                            )}
                                        >
                                            {needsSelectOption &&
                                                item['select']}
                                            {item[col?.field]}
                                            {needsOptions && item['options']}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </CellMeasurer>
            )
        }

        return (
            <div key={key} style={style}>
                {renderCard()}
            </div>
        )
    }

    return (
        <div className="w-full h-auto min-h-full flex flex-col items-center justify-start">
            {/* Header table */}
            <div className="w-full h-auto p-4 bg-white flex flex-col gap-4 items-start justify-start">
                <div className="w-full h-auto flex flex-col items-start justify-start gap-y-1">
                    {headerTitle && (
                        <p className="text-base font-medium text-black70">
                            {headerTitle}
                        </p>
                    )}
                    {headerSubtitle && (
                        <p className="text-sm font-normal text-textDarkGray">
                            {headerSubtitle}
                        </p>
                    )}
                </div>
                <div className="flex flex-row items-center justify-start w-full h-auto gap-x-2">
                    {headerActions?.length > 0 &&
                        headerActions
                            ?.filter(i => i != null)
                            ?.map(action => action)}
                </div>
            </div>
            {data?.length === 0 ? (
                <NotFoundResults cols={cols} query={query} />
            ) : (
                <div
                    className={clsx(
                        'w-full h-auto max-h-full flex-grow overflow-x-hidden bg-grayLines',
                        !isDashboardPage && 'min-h-96',
                    )}
                >
                    <ScrollSync>
                        {({ onScroll, scrollTop }) => (
                            <AutoSizer>
                                {({ width, height }) => (
                                    <List
                                        className="p-2"
                                        width={width}
                                        height={height}
                                        scrollTop={scrollTop}
                                        onScroll={onScroll}
                                        rowCount={data?.length}
                                        rowHeight={cache.rowHeight}
                                        rowRenderer={rowRenderer}
                                        scrollToAlignment="start"
                                    />
                                )}
                            </AutoSizer>
                        )}
                    </ScrollSync>
                </div>
            )}
        </div>
    )
}

MobileTable.propTypes = {
    headerTitle: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.any,
    ]),
    headerSubtitle: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.any,
    ]),
    headerActions: PropTypes.array,
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.any]),
    cols: PropTypes.oneOfType([PropTypes.array, PropTypes.any]),
    selectedItems: PropTypes.oneOfType([PropTypes.array, PropTypes.any]),
    query: PropTypes.string,
    isDashboardPage: PropTypes.bool,
    onSelectCard: PropTypes.func,
    selectable: PropTypes.bool,
}

export default MobileTable
